import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Icons } from '../../../shared/helpers/icons';
import { EDrillReportTypes } from '../../../shared/models/eop_types.enum';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { fileUploadValidator } from '../../../shared/helpers/fileUploadValidation';
import { UserDataService } from '../../../shared/services/userData.service';
import { DateUtils } from '../../../shared/helpers/dateUtils';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { DistrictCoordinatorTrainingService } from '../../../shared/services/districtCoordinatorTraining.service';

@Component({
  selector: 'new-training',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ReactiveFormsModule,
    SpinnerComponent
  ],
  templateUrl: './new-training.component.html',
  styleUrl: './new-training.component.css'
})
export class NewTrainingComponent implements OnInit {
  @ViewChild('datepickerStartInput') datepickerStartInput!: ElementRef;
  // @ViewChild('datepickerEndInput') datepickerEndInput!: ElementRef;
  
  icons = Icons;
  currentBuildingId: string | null = null;

  suggestions: string[] = [
    'Safety Training - ALSDE',
    'Safety Training - Alabama Emergency Management System',
    'Safety Training - Alabama Homeland Security Office',
    'Safety Training - Alabama School Resource Officer Association',
    'Safety Training - Federal Bureau of Investigation',
    'Safety Training - National School Resource Officer Association',
    'Safety Training - Partnership Alliance for Safe Schools',
    'Safety Training - Other Government Agency',
    'Safety Training - Non-Profit Organization',
    'Drills - Fire',
    'Drills - Lockdown',
    'Drills - Severe Weather',
    'EOP Meeting with Safety Officials',
    'Mapping Data meeting with Safety Officials'
  ];

  showSuggestions: boolean = false;
  loading: boolean = false;

  @Input() selectedDate: string | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  eventForm = new FormGroup({
    name: new FormControl('', Validators.required),
    start_date: new FormControl('', Validators.required),
    time: new FormControl('08:00', Validators.required),
    duration: new FormControl('1 hour', Validators.required),
    details: new FormControl('', Validators.required),
    files: new FormControl<File[]>([], fileUploadValidator())
  })

  constructor(
    private userDataService: UserDataService,
    private districtCoordinatorTrainingService: DistrictCoordinatorTrainingService
  ) {}

  ngOnInit(): void {
    if (this.selectedDate) {
      this.eventForm.get('start_date')?.setValue(this.selectedDate);
    }
    this.currentBuildingId = this.userDataService.getBuildingId();
  }
  
  async ngAfterViewInit(): Promise<void> {
    // Dynamically import the Datepicker to avoid server-side loading
    const { Datepicker } = await import('flowbite-datepicker');
    if (this.datepickerStartInput) {
      new Datepicker(this.datepickerStartInput.nativeElement, {
        autohide: true,
        format: 'mm/dd/yyyy'
      });
      if (this.selectedDate) { // Check if selectedDate is set, make it a default value for start date
        this.datepickerStartInput.nativeElement.value = DateUtils.formatDateForDisplay(this.selectedDate);
      }
      // Add event listener for date change
      // This will update the form control with the formatted date when the date is changed
      this.datepickerStartInput.nativeElement.addEventListener('changeDate', (event: any) => {
        const formattedDate = DateUtils.formatDateForBackend(event.target.value);
        this.eventForm.get('start_date')?.setValue(formattedDate);
      });
    }
    // if (this.datepickerEndInput) {
    //   new Datepicker(this.datepickerEndInput.nativeElement, {
    //     autohide: true,
    //     format: 'mm/dd/yyyy'
    //   });
    //   if (this.selectedDate) {
    //     this.datepickerEndInput.nativeElement.value = DateUtils.formatDateForDisplay(this.selectedDate);
    //   }
    //   this.datepickerEndInput.nativeElement.addEventListener('changeDate', (event: any) => {
    //     const formattedDate = DateUtils.formatDateForBackend(event.target.value);
    //     this.eventForm.get('end_date')?.setValue(formattedDate);
    //   });
    // }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      this.eventForm.get('files')?.setValue(files);
      this.eventForm.get('files')?.markAsDirty();
      this.eventForm.get('files')?.updateValueAndValidity();
    }
  }

  get filteredSuggestions(): string[] {
    const query = this.eventForm.get('name')?.value?.toLowerCase() || '';
    return this.suggestions.filter(s => s.toLowerCase().includes(query));
  }
  
  selectSuggestion(suggestion: string): void {
    this.eventForm.get('name')?.setValue(suggestion);
    this.showSuggestions = false;
  }
  
  hideSuggestions(): void {
    setTimeout(() => this.showSuggestions = false, 150);
  }
  
  saveNew(): void {
    if (this.eventForm.valid && this.currentBuildingId) {
      this.loading = true;
      const eventForm = new FormData();
  
      // Convert the form group to FormData
      Object.keys(this.eventForm.controls).forEach((key) => {
        const controlValue = this.eventForm.get(key)?.value;
    
        if (key === 'files') {
            if (controlValue && controlValue.length > 0) {
                // Handle file upload for 'files' field
                controlValue.forEach((file: File) => {
                    if (file instanceof Blob) {
                        eventForm.append(key, file, file.name); // Append each file
                    } else {
                        console.error("Invalid file type detected:", file);
                    }
                });
            }
        // If no files are present, do not append anything for 'files'.
        } else {
            // Add other form fields (non-file fields)
            if (controlValue !== undefined && controlValue !== null) {
                eventForm.append(key, controlValue);
            } else {
                // If the field is null or undefined, append an empty string to match FastAPI's expectation
                eventForm.append(key, '');
            }
        }
    });
  
      this.districtCoordinatorTrainingService.createNewCoordinatorTraining(this.currentBuildingId, eventForm).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.onSave.emit({ type: 'success', message: 'This occurred after creating a new event.' });
          }
          console.log(response);
        },
        error: (error) => {
          this.loading = false;
          this.onSave.emit({ type: 'unsuccess', message: error.message });
        }
      })
    } 
  }

  closeCreate(): void {
    this.onClose.emit();
  }

}
